import { createRouter, RouterProvider } from '@tanstack/react-router';
import { StrictMode, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { AssistantProvider } from '@/ai/assistant-context';
import { SessionProvider, useSession } from '@/auth/session-context';
import { ErrorBoundary } from '@/components/error-boundary';
import { OpeningSplash } from '@/components/opening-splash';
import { PaddleRecovery } from '@/components/paddle-recovery';
import { PlanLimitProvider } from '@/components/plan-limit-dialog';
import { ThemeProvider } from '@/components/theme-provider';
import { ToastProvider } from '@/components/toast';
import { setAnalyticsIdentity, startAnalytics, trackPageviews } from '@/lib/posthog';
import { startShellUpdates } from '@/lib/shell-update';
import { installSoftwareKeyboardTracker } from '@/lib/software-keyboard';
import { WorkspaceProvider } from '@/store/workspace-context';
import { SyncProvider } from '@/sync/sync-context';
import { routerDefaults } from './router-defaults';
import { routeTree } from './routeTree.gen';
import './styles.css';

const router = createRouter({ routeTree, ...routerDefaults });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

/**
 * Provider order, and why.
 *
 * `SessionProvider` is outermost because the identity it resolves decides
 * *which* workspace `WorkspaceProvider` opens — guest data and each account's
 * data are separate IndexedDB records. `SyncProvider` is innermost because it
 * needs both a session and a hydrated store before it may open a socket.
 *
 * `PlanLimitProvider` sits above `SyncProvider` because the sync client is what
 * *raises* a plan rejection and the shell is what renders it — the two never
 * meet, which is the point (PLAN §9).
 *
 * The one blocking wait in the app is the session: rendering the workspace
 * before it resolves would flash the guest workspace at a signed-in user. The
 * request is same-origin and hard-timeouts to "guest" (see `AUTH_TIMEOUT_MS`),
 * so this cannot become an indefinite splash screen.
 */
function App() {
  const session = useSession();

  /**
   * Turn on analytics, replay and error tracking once the deployment has said
   * whether it has any (PLAN-REMAINING O1).
   *
   * Here rather than at module scope because the key arrives on `/api/config`
   * and nothing in the bundle knows it beforehand — which is the point: `null`
   * means `startAnalytics` imports no SDK, so an unconfigured deployment pays
   * nothing. The call is idempotent, so the effect needs no guard beyond its
   * dependency.
   */
  useEffect(() => {
    // Fire and forget: the returned promise never rejects, and nothing in the
    // app waits on telemetry being up.
    void startAnalytics(session.config.analytics);
  }, [session.config.analytics]);

  /**
   * Tell PostHog which account this is, once the session has resolved.
   *
   * The id and nothing else — see `lib/posthog.ts` for why no email or name
   * goes with it. `loading` is skipped rather than reported as a signed-out
   * session, because "we have not asked yet" and "there is nobody" are
   * different facts and the second one calls `reset()`. Nothing here reads or
   * touches the entitlement: what a user *may do* is the server's business and
   * is not a person property.
   */
  useEffect(() => {
    if (session.status === 'loading') return;
    setAnalyticsIdentity(session.user?.id ?? null);
  }, [session.status, session.user?.id]);

  if (session.status === 'loading') {
    return <OpeningSplash label="Opening Beerwright…" />;
  }

  return (
    <>
      {/* Above the router and outside the workspace on purpose: a Paddle
          recovery link can land on any route, signed in or not, and needs only
          the session (for the client token) and the toast stack. */}
      <PaddleRecovery />
      <WorkspaceProvider identity={session.user?.id ?? null}>
        <PlanLimitProvider>
          <SyncProvider>
            {/* Outside the router so the drawer's thread survives navigation, and
              inside the workspace so a proposal card can dispatch (PLAN §7.1). */}
            <AssistantProvider>
              <RouterProvider router={router} />
            </AssistantProvider>
          </SyncProvider>
        </PlanLimitProvider>
      </WorkspaceProvider>
    </>
  );
}

const rootElement = document.getElementById('root');
if (rootElement === null) {
  throw new Error('#root is missing from index.html');
}

/**
 * {@link ErrorBoundary} is outside every provider, deliberately.
 *
 * A boundary only catches what is *below* it, and a provider throwing during
 * its own first render is exactly the crash that leaves a blank page. Its
 * fallback needs no provider to look right: the resolved theme lives on
 * `<html class="dark">`, which the inline script in `index.html` sets before
 * first paint, so the styles are correct even when `ThemeProvider` is the thing
 * that failed.
 */
createRoot(rootElement).render(
  <StrictMode>
    <ErrorBoundary>
      <ThemeProvider>
        <ToastProvider>
          <SessionProvider>
            <App />
          </SessionProvider>
        </ToastProvider>
      </ThemeProvider>
    </ErrorBoundary>
  </StrictMode>,
);

/**
 * Publish the visual viewport as custom properties, once for the document.
 *
 * Outside React because the thing it measures is the document, not a
 * component: the properties it writes on `<html>` are read by the bottom
 * sheet, the assistant drawer and the vitals pin, none of which know about
 * each other. It writes nothing where the browser has no `visualViewport`, so
 * a desktop is untouched.
 */
installSoftwareKeyboardTracker();

startShellUpdates();

/**
 * `$pageview` for every SPA navigation, subscribed once to the router itself.
 *
 * Here rather than in a route or a provider because the router outlives both,
 * and because a per-route hook is a thing a new route can forget to add. It is
 * safe to call before the SDK exists — and on a deployment where it never
 * will — since the subscription only reaches PostHog once `startAnalytics` has
 * loaded it (see `lib/posthog.ts`).
 */
trackPageviews(router);
